import JSZip from 'jszip'

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function escapeXml (value) {
  return String(value ?? '')
    // Excel rejects XML 1.0 control characters, including in headers.
    .replace(/[^\u0009\u000A\u000D\u0020-\uD7FF\uE000-\uFFFD]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function columnName (index) {
  let number = index + 1
  let name = ''
  while (number > 0) {
    const remainder = (number - 1) % 26
    name = String.fromCharCode(65 + remainder) + name
    number = Math.floor((number - 1) / 26)
  }
  return name
}

function sheetName (title) {
  const safe = String(title || 'Export')
    .replace(/[\[\]:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31)
  return escapeXml(safe || 'Export')
}

function cellXml (value, reference, style, sharedStringIndexes) {
  const stringValue = String(value ?? '')
  return `<c r="${reference}" t="s" s="${style}"><v>${sharedStringIndexes.get(stringValue)}</v></c>`
}

function worksheetXml (headers, rows, getCell, sharedStringIndexes) {
  const values = [
    headers.map((header) => header.title ?? ''),
    ...rows.map((row) => headers.map((header) => getCell(row, header.key, header)))
  ]
  const lastColumn = columnName(Math.max(0, headers.length - 1))
  const lastRow = Math.max(1, values.length)
  const worksheetRows = values.map((row, rowIndex) => {
    const rowNumber = rowIndex + 1
    const cells = row.map((value, colIndex) =>
      cellXml(
        value,
        `${columnName(colIndex)}${rowNumber}`,
        rowIndex === 0 ? 1 : (rowIndex % 2 === 0 ? 2 : 0),
        sharedStringIndexes
      )
    ).join('')
    const height = rowIndex === 0 ? ' ht="22" customHeight="1"' : ''
    return `<row r="${rowNumber}"${height}>${cells}</row>`
  }).join('')
  const widths = headers.map((_, colIndex) => {
    const width = Math.max(
      12,
      Math.min(42, Math.max(...values.map((row) => String(row[colIndex] ?? '').length), 0) + 2)
    )
    return `<col min="${colIndex + 1}" max="${colIndex + 1}" width="${width}" customWidth="1"/>`
  }).join('')

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastColumn}${lastRow}"/>
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>
      <selection pane="bottomLeft" activeCell="A2" sqref="A2"/>
    </sheetView>
  </sheetViews>
  <sheetFormatPr defaultRowHeight="18"/>
  <cols>${widths}</cols>
  <sheetData>${worksheetRows}</sheetData>
  <autoFilter ref="A1:${lastColumn}${lastRow}"/>
</worksheet>`
}

function sharedStringsXml (values) {
  const uniqueValues = []
  const seen = new Set()
  values.forEach((value) => {
    const stringValue = String(value ?? '')
    if (seen.has(stringValue)) return
    seen.add(stringValue)
    uniqueValues.push(stringValue)
  })

  return {
    indexes: new Map(uniqueValues.map((value, index) => [value, index])),
    xml: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${values.length}" uniqueCount="${uniqueValues.length}">
${uniqueValues.map((value) => `  <si><t xml:space="preserve">${escapeXml(value)}</t></si>`).join('\n')}
</sst>`
  }
}

function stylesXml () {
  // Keep element order schema-valid: font children must be b/sz/color/name/family.
  // Omit empty numFmts/dxfs — Excel for Mac repairs those and drops header fills,
  // which leaves white header text looking blurry/washed out.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><color rgb="FF172B36"/><name val="Calibri"/><family val="2"/></font>
    <font><b val="1"/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF0B607E"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF1F7F9"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFC9D8DF"/></left>
      <right style="thin"><color rgb="FFC9D8DF"/></right>
      <top style="thin"><color rgb="FFC9D8DF"/></top>
      <bottom style="thin"><color rgb="FFC9D8DF"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="3">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0"/>
  </cellStyles>
</styleSheet>`
}

function corePropertiesXml (title) {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${escapeXml(title || 'Export')}</dc:title><dc:creator>ProDIGIQ</dc:creator><cp:lastModifiedBy>ProDIGIQ</cp:lastModifiedBy></cp:coreProperties>`
}

function appPropertiesXml () {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>ProDIGIQ Lease Management System</Application></Properties>`
}

function normalizedFilename (filename) {
  const value = String(filename || 'export.xlsx')
  return /\.xlsx$/i.test(value) ? value : value.replace(/\.(?:xls|csv)$/i, '') + '.xlsx'
}

function stripZipDirectories (zip) {
  // JSZip creates empty directory entries for nested paths. Excel (especially on
  // Mac) treats those as corrupt package parts and repairs the workbook, which
  // is what produces the "We found a problem with some content" banner.
  Object.keys(zip.files).forEach((key) => {
    if (zip.files[key].dir) delete zip.files[key]
  })
}

/**
 * Download a real OOXML workbook instead of CSV content mislabeled as .xls.
 * Keeping this shared ensures modern index tables all use the same Excel path.
 */
export async function exportXlsx ({ title, headers = [], rows = [], filename = 'export.xlsx', getCell }) {
  const columns = headers.map((header) => ({
    key: header.key ?? header.name,
    title: header.title ?? header.label ?? ''
  }))
  const readCell = getCell || ((row, key) => row?.[key] ?? '')
  const values = [
    columns.map((column) => column.title),
    ...rows.map((row) => columns.map((column) => readCell(row, column.key, column)))
  ]
  const sharedStrings = sharedStringsXml(values.flat())
  const zip = new JSZip()

  zip.file('[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`)
  zip.file('_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`)
  zip.file('xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <fileVersion appName="xl" lastEdited="5" lowestEdited="5" rupBuild="9303"/>
  <workbookPr defaultThemeVersion="164011"/>
  <bookViews><workbookView xWindow="0" yWindow="0" windowWidth="24000" windowHeight="12000"/></bookViews>
  <sheets><sheet name="${sheetName(title)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`)
  zip.file('xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>
</Relationships>`)
  zip.file('xl/worksheets/sheet1.xml', worksheetXml(columns, rows, readCell, sharedStrings.indexes))
  zip.file('xl/styles.xml', stylesXml())
  zip.file('xl/sharedStrings.xml', sharedStrings.xml)
  zip.file('docProps/core.xml', corePropertiesXml(title))
  zip.file('docProps/app.xml', appPropertiesXml())

  stripZipDirectories(zip)

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: XLSX_MIME_TYPE,
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = normalizedFilename(filename)
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
